import { PricingPlan } from '@atlassiansox/asc-product-catalog';
import { PricingStrategy, PricingTier } from '@atlassiansox/asc-product-catalog';

import { PricingBreakdown, PricingBreakdownItem } from '../models';
import { Predicate } from './predicate';
import { getUnitPrice } from './pricing-tier';

export type PricingTierToPricingBreakdownItemTransform = (
  tier: PricingTier,
  index: number
) => PricingBreakdownItem;

export function createMarginalPerUnitBreakdown(
  pricingPlan: PricingPlan,
  units: number,
  currencyIsoCode: string
): PricingBreakdown {
  return pricingPlan
    .filter(createPricingTierIsMarginalPerUnitStrategy(units))
    .sort(comparePricingTierByUnitLimitLower)
    .map(
      createPricingTierToPricingBreakdownItemTransform(units, currencyIsoCode)
    );
}

export function comparePricingTierByUnitLimitLower(
  { unitLimitLower: firstLimit }: PricingTier,
  { unitLimitLower: secondLimit }: PricingTier
): number {
  if (firstLimit !== null && secondLimit !== null) {
    return firstLimit - secondLimit;
  } else if (firstLimit === null) {
    return 1;
  } else {
    return -1;
  }
}

export function createPricingTierToPricingBreakdownItemTransform(
  units: number,
  currencyIsoCode: string
): PricingTierToPricingBreakdownItemTransform {
  return (tier, index) => {
    const unitPrice = getUnitPrice(tier, currencyIsoCode);
    const ignoreUnitLimitLower = index === 0;
    
    if (ignoreUnitLimitLower) {
      return {
        units: Math.min(units, tier.unitLimitUpper || Infinity),
        unitPrice,
        tier: {
          ...tier,
          unitLimitLower: 1
        }
      };
    } else {
      const unitLimitLower = tier.unitLimitLower || 1;
      if (units < unitLimitLower) {
        return { units: 0, unitPrice, tier };
      } else {
        return {
          units:
            Math.min(units, tier.unitLimitUpper || Infinity) -
            unitLimitLower +
            1,
          unitPrice,
          tier
        };
      }
    }
  };
}

function createPricingTierIsMarginalPerUnitStrategy(
  units: number
): Predicate<PricingTier> {
  return ({ pricingStrategy, unitLimitLower }) =>
    pricingStrategy === PricingStrategy.MARGINAL_PER_UNIT &&
    (unitLimitLower === null || units >= unitLimitLower);
}
